import { useThree, useFrame } from "@react-three/fiber";
import { useRef, useState, useEffect, useMemo } from "react";
import { MotionSequencer } from "../components/motion/MotionSequencer";
import { useMotionStore } from "../components/motion/useMotionStore";
import { Splat, type CameraControls } from "@react-three/drei";
import { SparkSplat } from "../components/spark/SparkSplat";
import { BezierCurveDebug } from "../components/motion/BezierCurveDebug";
import { useVideoRecorder } from "../hooks/useVideoRecorder";
import * as THREE from "three";

interface SceneProps {
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  onSequenceComplete?: () => void;
  onSequenceCompleteRef?: React.MutableRefObject<(() => void) | undefined>;
}

/**
 * Separate `Scene` component to be used in the React Three Fiber `Canvas` component so that we can use React Three Fiber hooks like `useThree`
 */
export const Scene = ({ isPlaying, setIsPlaying, onSequenceComplete, onSequenceCompleteRef }: SceneProps) => {
  const renderer = useThree((state) => state.gl);
  const size = useThree((state) => state.size);
  const [replayKey, setReplayKey] = useState(0); // Key to force re-render of MotionSequencer
  
  // Get blocks from store and setControls function
  const { blocks, setControls, setVideoRecorder, modelUrl, modelFileExtension } = useMotionStore();
  
  // Get CameraControls instance from R3F
  const controls = useThree((state) => state.controls as CameraControls | null);

  // Initialize video recorder
  const videoRecorder = useVideoRecorder(renderer, {
    width: size.width,
    height: size.height,
    fps: 30,
    bitrate: 10_000_000,
  });

  // Expose video recorder to store and update when state changes
  useEffect(() => {
    // Always update the recorder in the store when it's available
    // This ensures the UI reflects the current recording state
    if (videoRecorder) {
      setVideoRecorder(videoRecorder);
    }
  }, [videoRecorder.isRecording, videoRecorder.isProcessing, videoRecorder.progress, setVideoRecorder]);
  
  // Track key states for smooth roll control
  const keysPressed = useRef<Set<string>>(new Set());

  // Register controls to store when available
  useEffect(() => {
    if (controls) {
      setControls(controls);
    }
  }, [controls, setControls]);
  
  // Smooth roll rotation using useFrame
  useFrame((_state, delta) => {
    if (!controls || isPlaying || keysPressed.current.size === 0) return;
    
    // Roll speed in radians per second
    const rollSpeed = Math.PI / 2; // 90 degrees per second
    const rollDelta = rollSpeed * delta;
    
    // Check if A or D is pressed
    const rollDirection = keysPressed.current.has('a') ? -1 : 
                         keysPressed.current.has('d') ? 1 : 0;
    
    if (rollDirection !== 0) {
      // Get camera forward direction
      const forward = new THREE.Vector3();
      controls.camera.getWorldDirection(forward);
      
      // Get current up vector
      const currentUp = controls.camera.up.clone();
      
      // Create rotation matrix around forward axis
      const rotationMatrix = new THREE.Matrix4().makeRotationAxis(forward, rollDirection * rollDelta);
      
      // Apply rotation to up vector
      const newUp = currentUp.applyMatrix4(rotationMatrix);
      controls.camera.up.copy(newUp);
      controls.camera.updateProjectionMatrix();
    }
  });

  // Replay function - resets and restarts the sequence
  const replaySequence = () => {
    setIsPlaying(false);
    // Use a small timeout to ensure the sequence stops before restarting
    setTimeout(() => {
      setReplayKey(prev => prev + 1); // Force re-render
      setIsPlaying(true);
    }, 50);
  };

  // Keyboard event listener for "P" key to replay and A/D keys for roll
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      
      // Check if "P" key is pressed (case-insensitive)
      if (key === 'p') {
        event.preventDefault();
        replaySequence();
        return;
      }

      // Track A/D keys for smooth roll rotation
      // Only allow roll adjustment when not playing
      if (!isPlaying && (key === 'a' || key === 'd')) {
        event.preventDefault();
        keysPressed.current.add(key);
      }
    };
    
    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      
      // Remove A/D keys from pressed set
      if (key === 'a' || key === 'd') {
        event.preventDefault();
        keysPressed.current.delete(key);
      }
    };

    // Add event listeners
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Cleanup: remove event listeners on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      keysPressed.current.clear();
    };
  }, [isPlaying, setIsPlaying]); // Include isPlaying in dependencies

  // Determine which renderer to use based on file extension
  const isPlyFile = useMemo(() => {
    return modelFileExtension === '.ply';
  }, [modelFileExtension]);

  const meshRef = useRef<any>(null);

  return (
    <>
      <MotionSequencer 
        key={replayKey} // Force re-mount when replayKey changes
        isPlaying={isPlaying}
        sequenceIds={blocks.length > 0 ? blocks : []} // Use blocks from store
        modelRadius={2}
        onComplete={() => {
          setIsPlaying(false);
          // Get the latest callback from ref if available, otherwise use prop
          const callback = onSequenceCompleteRef?.current || onSequenceComplete;
          if (callback && typeof callback === 'function') {
            callback();
          }
        }}
      />
      <BezierCurveDebug />

      {isPlyFile ? (
        <SparkSplat meshRef={meshRef} />
      ) : (
        <Splat src={modelUrl} position={[0, 0, 0]} key={modelUrl} />
      )}
    </>
  );
};

